import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { LocalizedText } from '@/components/PlanCard'

/**
 * What `GET /join/:token` answers. `loginPage` carries the organisation's sign-in page
 * texts and whether a logo can be fetched from `GET /join/:token/logo`; the payload has
 * no ids on purpose.
 */
export interface JoinLinkContext {
  valid: boolean
  organisationName?: string
  modules?: { name: string }[]
  loginPage?: {
    subtitle?: LocalizedText
    lede?: LocalizedText
    contactEmail?: string
    hasLogo?: boolean
  }
}

export interface SignInBrand {
  name: string
  /** Null: the tile shows the organisation's initials, or the Georgia mark when `isGeorgia`. */
  logoUrl: string | null
  isGeorgia: boolean
  subtitle: string
  lede: string
  contactEmail: string
}

export type SignInBrandState = { status: 'loading' } | { status: 'ready'; brand: SignInBrand; modules: string[] }

/** The organisation's text in the visitor's language, else its English text. */
const pickText = (text: LocalizedText | undefined, language: string): string | undefined => {
  if (!text) return undefined
  const short = language.split('-')[0]
  return text[short as 'en' | 'fr']?.trim() || text.en?.trim() || undefined
}

/**
 * Which customer the sign-in page is for. The one source is the join token: the page
 * is branded for the link's organisation and otherwise shows Georgia itself. Nothing
 * is read from storage or the hostname (decided 2026-11-01).
 *
 * With a token the page waits for the join context, so the visitor never sees the
 * Georgia version first. Without one there is nothing to wait for.
 */
export const useSignInBrand = (token: string | null, joinContext: JoinLinkContext | null): SignInBrandState => {
  const { t, i18n } = useTranslation()
  const language = i18n.language || 'en'

  return useMemo(() => {
    const defaults = {
      subtitle: t('auth.page.subtitle'),
      lede: t('auth.page.lede'),
      contactEmail: t('auth.page.contactEmail'),
    }
    const georgia: SignInBrand = { name: 'Georgia', logoUrl: null, isGeorgia: true, ...defaults }

    if (!token) return { status: 'ready', brand: georgia, modules: [] }
    if (!joinContext) return { status: 'loading' }
    if (!joinContext.valid || !joinContext.organisationName) return { status: 'ready', brand: georgia, modules: [] }

    const page = joinContext.loginPage
    return {
      status: 'ready',
      brand: {
        name: joinContext.organisationName,
        logoUrl: page?.hasLogo ? `${process.env.VITE_API_URL}/join/${encodeURIComponent(token)}/logo` : null,
        isGeorgia: false,
        subtitle: pickText(page?.subtitle, language) ?? defaults.subtitle,
        lede: pickText(page?.lede, language) ?? defaults.lede,
        contactEmail: page?.contactEmail?.trim() || defaults.contactEmail,
      },
      modules: (joinContext.modules ?? []).map((m) => m.name),
    }
  }, [token, joinContext, language, t])
}

export default useSignInBrand
